import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { apiFetch } from '@/lib/api-fetch';
import { getVaultBlob, getVaultMetadata, setServerLastModified, setVaultSyncId } from '../../utils/vault-storage';
import type { VaultUploadResponse, VaultRegisterResponse } from '@syfo/types/api.client.types';

/**
 * Custom error class for sync conflicts
 */
export class SyncConflictError extends Error {
  constructor(
    message: string,
    public vaultId: string,
    public serverLastModified?: string
  ) {
    super(message);
    this.name = 'SyncConflictError';
  }
}

/**
 * Custom error class for vault deleted on server
 */
export class VaultDeletedError extends Error {
  constructor(
    message: string,
    public vaultId: string
  ) {
    super(message);
    this.name = 'VaultDeletedError';
  }
}

/**
 * Upload vault data to server via POST API
 * Returns lastModified from server response
 * Throws SyncConflictError on 412 Precondition Failed
 * Throws VaultDeletedError on 404 Not Found
 */
const uploadToServer = async (
  vaultId: string,
  encryptedData: string,
  currentLastModified?: string,
  forceUpload: boolean = false
): Promise<{ lastModified: string; newSyncId?: string }> => {
  // Prepare headers
  const headers: HeadersInit = {
    'Content-Type': 'text/plain'
  };

  // Add If-Unmodified-Since header for conditional updates (when lastModified exists and not forcing)
  if (currentLastModified && !forceUpload) {
    headers['If-Unmodified-Since'] = currentLastModified;
  }

  // Make API call - POST with base64-encoded encrypted data as body
  const response = await apiFetch(`/api/vaults/${vaultId}`, {
    method: 'POST',
    headers,
    body: encryptedData // Already base64 encoded in localStorage
  });

  // Handle not found (vault deleted on server)
  if (response.status === 404) {
    if (forceUpload) {
      return registerAsNewVault(encryptedData);
    }
    throw new VaultDeletedError(
      'Vault has been deleted on the server',
      vaultId
    );
  }

  // Handle precondition failed (date mismatch — server has been modified since our last sync)
  if (response.status === 412) {
    const serverLastModified = response.headers.get('Last-Modified') || undefined;
    throw new SyncConflictError(
      `Vault has been modified on another device [vaultId:${vaultId}]`,
      vaultId,
      serverLastModified
    );
  }

  // Handle other errors
  if (!response.ok) {
    throw new Error(`Failed to upload vault: ${response.status} ${response.statusText}`);
  }

  // Parse response
  const result: VaultUploadResponse = await response.json();
  return { lastModified: result.lastModified };
};

/**
 * Register a vault as new via POST /api/vaults (the register endpoint).
 * Used as a fallback when a force-upload discovers the vault was deleted.
 */
const registerAsNewVault = async (
  encryptedData: string
): Promise<{ lastModified: string; newSyncId: string }> => {
  const response = await apiFetch('/api/vaults', {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: encryptedData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to re-register vault: ${response.status} ${errorText}`);
  }

  const result: VaultRegisterResponse = await response.json();
  return { lastModified: result.lastModified, newSyncId: result.vaultId };
};

/**
 * Thunk to upload a single vault to server (POST)
 * Called by middleware when vault data changes locally
 * Uses syncId (server-assigned ID) for API calls
 * Returns the new lastModified from the server and vaultId
 */
export const uploadVaultThunk = createAsyncThunk<
  { lastModified: string; vaultId: string; newSyncId?: string },
  { vaultId: string; forceUpload?: boolean },
  { state: RootState }
>(
  'vault/upload',
  async ({ vaultId, forceUpload = false }) => {
    // Get encrypted data from localStorage
    const encryptedData = getVaultBlob(vaultId);
    
    if (!encryptedData) {
      throw new Error('No vault data found to upload');
    }

    // Get vault metadata
    const metadata = getVaultMetadata(vaultId);
    if (!metadata) {
      throw new Error('No vault metadata found to upload');
    }

    // Get syncId - required for server API calls
    const syncId = metadata.syncId;
    if (!syncId) {
      throw new Error('Vault not registered with server. Enable sync first.');
    }

    // API call with conditional header handling - uses syncId for server URL
    const result = await uploadToServer(
      syncId, 
      encryptedData, 
      metadata.serverLastModified,
      forceUpload
    );
    
    // If the vault was re-registered (deleted then force-uploaded), update syncId
    if (result.newSyncId) {
      setVaultSyncId(vaultId, result.newSyncId);
    }

    // Store the new lastModified in localStorage (keyed by client vaultId)
    setServerLastModified(vaultId, result.lastModified);
    return { lastModified: result.lastModified, vaultId, newSyncId: result.newSyncId };
  }
);
